type limitinitLimitsByGroup = [string, number][];
type getEntityById<EntityIdType, EntityType> = (id: EntityIdType) => EntityType;
type getGroupFromEntity<EntityType> = (entity: EntityType) => string;
type toggleEntityState<EntityType> = (entity: EntityType, isBlocked: boolean) => void;
type getEntityState<EntityType> = (entity: EntityType) => boolean;
export class LimitQueue<EntityIdType extends string | number, EntityType> {
	limitStateByGroups: Map<
		string,
		{
			amount: number;
			blockedAmount: number;
		}
	>;
	limitsByGroups: Map<string, number>;
	entitiesByGroups: Map<string, Set<EntityIdType>>;
	getEntityById: getEntityById<EntityIdType, EntityType>;
	getGroupFromEntity: getGroupFromEntity<EntityType>;
	getEntityState: getEntityState<EntityType>;
	toggleEntityState: toggleEntityState<EntityType>;

	constructor(
		getEntityById: getEntityById<EntityIdType, EntityType>,
		getGroupFromEntity: getGroupFromEntity<EntityType>,
		toggleEntityState: toggleEntityState<EntityType>,
		getEntityState: getEntityState<EntityType>,
		limits: limitinitLimitsByGroup
	) {
		this.entitiesByGroups = new Map();
		this.limitsByGroups = new Map();
		this.limitStateByGroups = new Map();
		this.getEntityById = getEntityById;
		this.initLimits(limits);
		this.getGroupFromEntity = getGroupFromEntity;
		this.getEntityState = getEntityState;
		this.toggleEntityState = toggleEntityState;
	}
	initLimits(limits: limitinitLimitsByGroup) {
		for (const [group, limit] of limits) {
			this.limitsByGroups.set(group, limit);
			this.limitStateByGroups.set(group, {
				amount: 0,
				blockedAmount: 0
			});
			this.entitiesByGroups.set(group, new Set());
		}
	}
	changeEntityBlockState(group: string, amount: number, isNeedBlocked: boolean) {
		if (amount === 0) {
			return;
		}
		const initState = amount;
		const entitiesIds = [...this.entitiesByGroups.get(group)!.values()];
		for (let index = entitiesIds.length - 1; index >= 0; index--) {
			const entityId = entitiesIds[index];
			if (amount === 0) {
				break;
			}
			const entity = this.getEntityById(entityId);
			const isBlocked = this.getEntityState(entity);
			if (isBlocked === isNeedBlocked) continue;
			amount -= 1;
			this.toggleEntityState(entity, isNeedBlocked);
		}
		const groupState = this.limitStateByGroups.get(group)!;
		if (isNeedBlocked) {
			groupState.blockedAmount += initState - amount;
			return;
		}
		groupState.blockedAmount -= initState - amount;
	}

	changeBlockStateToLimits() {
		for (const key of this.limitsByGroups.keys()) {
			const targetLimit = this.limitsByGroups.get(key)!;
			const { amount, blockedAmount } = this.limitStateByGroups.get(key)!;
			const unBlockedAmount = amount - blockedAmount;
			if (unBlockedAmount < targetLimit) {
				const needToUnblock = targetLimit - unBlockedAmount;
				if (needToUnblock >= blockedAmount) {
					this.changeEntityBlockState(key, blockedAmount, false);
					continue;
				}
				this.changeEntityBlockState(key, needToUnblock, false);
				continue;
			}
			const needToBlock = unBlockedAmount - targetLimit;
			this.changeEntityBlockState(key, needToBlock, true);
		}
	}
	addRemoveEntities(entitesIds: EntityIdType[], isAdd: boolean) {
		for (const entityId of entitesIds) {
			const entity = this.getEntityById(entityId);
			const isBlocked = this.getEntityState(entity);
			const entityGroup = this.getGroupFromEntity(entity);
			const state = this.limitStateByGroups.get(entityGroup)!;
			if (!this.limitsByGroups.has(entityGroup)) {
				continue;
			}
			if (isAdd) {
				state.amount += 1;
				if (isBlocked) {
					state.blockedAmount += 1;
				}
				this.entitiesByGroups.get(entityGroup)?.add(entityId);
				continue;
			}
			if (isBlocked) {
				state.blockedAmount -= 1;
			}
			this.limitStateByGroups.get(entityGroup)!.amount -= 1;
			this.entitiesByGroups.get(entityGroup)?.delete(entityId);
		}
		this.changeBlockStateToLimits();
	}
}
